import React from 'react';
import { Code, Smartphone, Building2, ShoppingCart, Palette, Cloud } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { services } from '../data/mock';
import AnimatedSection from './AnimatedSection';

const iconMap = {
  Code: Code,
  Smartphone: Smartphone,
  Building2: Building2,
  ShoppingCart: ShoppingCart,
  Palette: Palette,
  Cloud: Cloud
};

const Services = () => {
  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimatedSection animation="fadeInUp">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-900 animate-pulse">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We offer comprehensive software development services to transform your business ideas 
              into powerful digital solutions that drive growth and efficiency.
            </p>
          </div>
        </AnimatedSection>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <AnimatedSection 
                key={service.id} 
                delay={index * 100}
                animation="fadeInUp"
              >
                <Card className="group hover:shadow-xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-200 bg-white overflow-hidden relative">
                  {/* Animated background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <CardHeader className="pb-4 relative z-10">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-all duration-300 group-hover:rotate-6 group-hover:scale-110">
                      <IconComponent className="h-6 w-6 text-blue-900 group-hover:animate-pulse" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-900 transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, featureIndex) => (
                        <Badge 
                          key={featureIndex} 
                          variant="secondary" 
                          className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200 hover:scale-105 animate-fade-in-badge"
                          style={{ animationDelay: `${featureIndex * 0.1}s` }}
                        >
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                  
                  {/* Animated border */}
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-200 rounded-lg transition-all duration-300"></div>
                </Card>
              </AnimatedSection>
            );
          })}
        </div>

        {/* CTA Section */}
        <AnimatedSection delay={800} animation="scaleIn">
          <div className="text-center mt-16">
            <div className="bg-gray-900 rounded-2xl p-8 md:p-12 relative overflow-hidden">
              {/* Animated background elements */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-20 h-20 bg-yellow-400 rounded-full animate-pulse"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
              
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 animate-fade-in-word">
                  Ready to Transform Your Business?
                </h3>
                <p className="text-gray-300 mb-8 text-lg max-w-2xl mx-auto animate-fade-in-word" style={{ animationDelay: '0.3s' }}>
                  Let's discuss how our services can help you achieve your digital goals. 
                  Get a free consultation and project estimate today.
                </p>
                <button 
                  className="bg-yellow-600 hover:bg-yellow-700 text-black font-bold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 text-lg animate-bounce-subtle"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Consultation
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Services;